import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';


import bcrypt from 'bcryptjs';
import { Menu, X, Users, BookOpen, Globe, Shield, Scale, Award, LogOut, CheckCircle, XCircle, Clock, Trash2, UserPlus, Mail, Calendar, ArrowRight, ChevronRight, Image as ImageIcon, Download, Facebook, Instagram, Linkedin } from 'lucide-react';
import { supabase } from './supabase';
const API_BASE = 'https://yiga-backend-1.onrender.com/api';

const teamPhotos = {
  heroImage: '/images/hero.jpg',
  aboutImage: '/images/about.jpg',
  meetingImage: '/images/meeting.jpg',
  eventImage: '/images/event.jpg',
  workshopImage: '/images/workshop.jpg',
  galleryImages: [
    '/images/gallery1.jpg',
    '/images/gallery2.jpg',
    '/images/gallery3.jpg',
    '/images/gallery4.jpg',
    '/images/gallery5.jpg',
    '/images/gallery6.jpg'
  ]
};

const teamMembers = [
  { name: "Jeremy Oronje", role: "Executive Director", department: "Leadership", bio: "Leading YIGA's vision and strategic direction", photo: "/images/Jeremy Oronje -Executive Director.jpeg" },
  { name: "Galdicia Wambui Gacihi", role: "Deputy Executive Director", department: "Leadership", bio: "Supporting executive leadership", photo: "/images/Deputy Executive Director.jpeg" },
  { name: "Ashley Munyasia", role: "Director of Research", department: "Research", bio: "Leading research initiatives", photo: "/images/Ashley Munyasia-Director of Research.jpeg" },
  { name: "Anne Ngoya", role: "Research Fellow", department: "Research", bio: "Contributing to research initiatives", photo: "/images/Anne Ngoya-Research Fellow.jpeg" },
  { name: "Benjamin Kitonga", role: "Research Fellow", department: "Research", bio: "Contributing to research initiatives", photo: "/images/Benjamin Kitonga-Research Fellow.jpeg" },
  { name: "Jared Nyakundi", role: "Research Fellow", department: "Research", bio: "Contributing to research initiatives", photo: "/images/Jared Nyakundi-Research Fellow.jpeg" },
  { name: "Michael Adiado", role: "Research Fellow", department: "Research", bio: "Contributing to research initiatives", photo: "/images/Michael Adiado-Research Fellow.jpeg" },
  { name: "Neema Toto", role: "Director of Events", department: "Events", bio: "Managing YIGA events", photo: "/images/Neema Toto-Director of Events.jpeg" },
  { name: "Neema Wanjiku", role: "Assistant Director of Events", department: "Events", bio: "Supporting events coordination", photo: "/images/Neema Wanjiku-Assistant Director Of Events.jpeg" },
  { name: "Cindy Waweru", role: "Events Assistant", department: "Events", bio: "Supporting events coordination", photo: "/images/Cindy Waweru-Events Assistant.png" },

  { name: "Kemunto Joylynn", role: "Director for Programs and Partnerships", department: "Programs and Partnerships", bio: "Leading program coordination", photo: "/images/Joylynne Kemunto -Assistant Director of Programs and Partnership.jpeg" },
  { name: "Hilda Koipano", role: "Director of Communications", department: "Communication", bio: "Leading communication strategies", photo: "/images/Hilda Koipano-Director of Communications.jpeg" },
  { name: "Stacy Mwogoi", role: "Assistant Director for Programs and Partnerships", department: "Programs and Partnerships", bio: "Supporting program coordination", photo: "/images/Stacy Mwogoi-Assistant Director of Programs and Partnerships.jpeg" },
  { name: "Zac Nganga", role: "Programs Assistant", department: "Programs and Partnerships", bio: "Supporting program delivery", photo: "/images/Zac Nganga-Programs Assistant.jpeg" },

  { name: "Abel Omenge", role: "Director of Finance", department: "Finance", bio: "Managing financial operations", photo: "/images/Abel Omenge-Director of Finance.jpeg" },

];

const articles = [
  {
    section: "article",
    id: 7,
    title: "Protecting Kenya's Children Online: The Case for a More Deliberate Approach",
    excerpt: "For many children, internet-connected devices are now part of everyday life. This article examines the risks of unrestricted access, from harmful content to online gambling, and sets out a more deliberate, age-appropriate approach for Kenya.",
    image: "https://images.unsplash.com/photo-1517574378266-220e185f7f8c?w=800&h=500&fit=crop",
    category: "Digital Policy",
    date: "October 2026",
    author: "Jared",
    readTime: "6 min read",
    tags: ["Child Online Safety", "Kenya", "Digital Policy", "Social Media"],
    fullContent: `## The case for a more deliberate approach

For many children, internet-connected devices are now part of everyday life. Smartphones and other digital devices can support learning, communication and access to information, but unrestricted access can also expose children to content and behaviours that may be inappropriate or harmful. The question for Kenya is therefore not whether children should benefit from digital technology, but how those benefits can be balanced with age-appropriate safeguards.

The discussion is particularly important for younger children, whose ability to regulate attention, screen use and online behaviour is still developing. Parents and caregivers may also find it difficult to manage access once children become accustomed to using phones as a source of entertainment or distraction. These concerns point to the need for a broader conversation about age limits, parental controls, digital literacy and platform responsibility.

## What research suggests about attention and online exposure

Research cited in the original article points to concerns about the relationship between social-media use and children's attention. A 2026 study associated with Sweden's Karolinska Institutet examined more than 8,000 children aged eight to 14 and reported concerns about the relationship between social-media use and attention. Such findings warrant attention, although they should not be interpreted as establishing that internet or social-media use alone causes declining concentration.

Other research cited by the author has also examined links between online exposure and issues including social anxiety, depression, attention-deficit/hyperactivity disorder (ADHD), sleep difficulties, aggressive behaviour, cyberbullying and sexual exploitation. These risks reinforce the importance of age-appropriate safeguards, particularly where children may encounter harmful content without adequate supervision.

## Children's exposure to harmful content

The risks extend beyond attention and screen time. Children can encounter pornography, violent or otherwise age-inappropriate material, as well as adults or peers who may exploit online platforms. The personal account cited in the original article, published by Parents Africa, illustrates how exposure to pornography can affect more than one child within a household. While an individual account cannot establish the prevalence of the problem, it demonstrates why parents and policy-makers should take online child protection seriously.

Effective protection cannot depend entirely on parents discovering harmful exposure after it has occurred. Age-appropriate content controls, parental supervision, safer platform design and accessible reporting mechanisms can form part of a broader child online-safety framework.

## Online gambling presents another concern

Online gambling is another area where easy access to smartphones can create risks for minors. The original article highlights the experience of a 16-year-old who reportedly used school fees for online betting after having previously engaged in gambling. The example illustrates a wider concern: repeated exposure to betting advertisements and easy access to online gambling platforms can normalise gambling among young people.

The article also cites a Daily Nation report stating that 76% of Kenyan youth aged 18 to 35 participate in gambling, with a substantial proportion occurring through online betting platforms. This statistic requires verification against the original report before publication, particularly because the cited age range begins at 18 and therefore cannot by itself demonstrate that gambling begins before the age of majority.

## Lessons from other countries

Kenya can also examine how other countries are responding to children's use of social media. The original article points to Australia's restrictions on social-media access for people below a specified age and to legislative developments in France. These approaches demonstrate that governments are increasingly considering stronger age-based safeguards for children online.

However, restrictions differ in scope, age thresholds and implementation mechanisms. Kenya should therefore assess the evidence behind these approaches rather than simply replicate them. Key considerations would include age verification, privacy, access to educational content, enforcement responsibilities and the risk of excluding children from legitimate digital services.

## A possible framework for Kenya

A blanket restriction on internet access would have significant implications because children increasingly rely on digital platforms for education, communication and access to public information. A more targeted framework could distinguish between general internet access and access to high-risk services such as social-media platforms, pornography and online gambling.

The original article proposes age-based daily limits similar to restrictions reported in China, alongside a ban on social-media access for children below the age of majority. If Kenya considers such measures, they should be supported by clear evidence, consultation with parents, educators, children's rights experts, technology companies and young people, and safeguards against unintended consequences.

Policy-makers could also consider stronger parental-control tools, default child-safety settings, age assurance for high-risk services, restrictions on targeted advertising to minors, stronger enforcement against child sexual exploitation online, and digital-literacy programmes for parents and children. These measures could complement rather than replace reasonable age restrictions.

## Protecting children without excluding them from opportunity

Kenya's digital transformation is an important opportunity for education, innovation and participation in the global economy. The objective should not be to keep children away from technology altogether, but to create an environment in which access is appropriate to their age and level of maturity.

Stronger safeguards should therefore be considered as part of a wider child online-safety policy. Kenya can learn from international experience while developing measures suited to its own legal, technological and social context. The central question is no longer whether children will use the internet, but whether the systems surrounding them are strong enough to make that use safer.

## References

Karolinska Institutet. 2026. "Using Social Media May Impair Children's Attention." https://news.ki.se/using-social-media-may-impair-childrens-attention

BBC News. 2025. Article on Australia's social-media restrictions for children. https://www.bbc.com/news/articles/cwyp9d3ddqyo

PBS NewsHour. 2026. Article on French lawmakers' social-media restrictions for children. https://www.pbs.org/newshour/world/french-lawmakers-approve-ban-on-social-media-for-kids-under-15

Parents Africa. "One Mum's Experience: A Fight Against Child Pornography." https://parentsafrica.com/one-mums-experience-a-fight-against-child-pornography/

Nature / Humanities and Social Sciences Communications. 2026. Article cited in the original draft on children, internet/social-media exposure and mental-health-related outcomes. https://www.nature.com/articles/s41599-026-06609-1

Daily Nation. Report cited in the original draft stating that 76% of Kenyan youth aged 18-35 participate in gambling. Original report/source details should be confirmed before publication.`,
    authorBio: "Jared is a qualified lawyer working in the legal industry, with a strong interest in international relations and alternative dispute resolution. He follows current affairs across Africa and globally and is based in Nairobi."
  },
  {
    section: "article",
    id: 5,
    title: "BATUK: Inside Britain's Military Footprint in Kenya and the Reckoning Reshaping It",
    excerpt: "For six decades, a British military presence near Nanyuki has trained thousands of soldiers a year on Kenyan soil. That arrangement is now being tested by a parliamentary inquiry, a stalled defence treaty, and a newly adopted foreign policy doctrine centred on sovereignty and accountability.",
    image: "https://images.unsplash.com/photo-1646159755791-54e741749028?w=800&h=500&fit=crop",
    category: "Peace & Security",
    date: "August 2026",
    author: "Benjamin Matei Kitonga",
    readTime: "10 min read",
    tags: ["BATUK", "Kenya-UK Relations", "Sovereignty", "Defence Policy"],
    fullContent: `For six decades, a British military presence near Nanyuki has trained thousands of soldiers a year on Kenyan soil. Today, that arrangement, long treated as a quiet fixture of the Kenya-UK relationship, is being pulled into the open, tested by a parliamentary inquiry, a stalled defence treaty, and a newly adopted foreign policy doctrine that puts sovereignty and accountability at its centre.

## How BATUK Came to Kenya

BATUK's presence in Kenya did not arrive by accident; it is a direct legacy of the country's transition out of colonial rule. The unit traces its origins to 1964, when a defence arrangement was struck between Britain and Kenya just as the country was gaining full independence, establishing the framework for continued British military training on Kenyan soil.

For its early years, the unit operated out of Nairobi before its operational focus shifted north. Its logistics hub, Kifaru Barracks, remains inside Kahawa Barracks in Nairobi, hosted by the Kenya Army, while its main training headquarters relocated to Nanyuki to be closer to the open terrain at the foothills of Mount Kenya that suits large-scale infantry exercises.

That original arrangement has evolved considerably since. It gave way to the 2015 Defence Cooperation Agreement (DCA), a legally binding treaty setting out the terms under which UK forces operate on Kenyan territory, including training areas, dispute settlement, taxation, vehicle registration, and entry procedures. The 2015 DCA also addressed jurisdictional questions, including Kenyan jurisdiction over criminal offences allegedly committed by UK military personnel while in the country.

The 2015 DCA expired in 2021. A successor agreement was negotiated but has never been formally ratified by Kenya's Parliament, leaving the legal basis for BATUK's presence, by lawmakers' own admission, in limbo for years.

## The Training Base

For decades, BATUK made do with rented land, leasing ground from the Nanyuki Agricultural Society and having to vacate every year for the local agricultural show. That changed with the construction of a purpose-built Â£70 million headquarters and training facility at Laikipia Air Base East, officially opened in 2021 by then UK Defence Secretary Ben Wallace alongside Kenya's then Cabinet Secretary for Defence, Dr Monica Juma. The new Nyati Barracks gave the unit permanent accommodation and facilities designed to help incoming troops acclimatise before training, replacing the temporary setup that had defined the base for years.

Today, Nyati Barracks houses BATUK's headquarters, accommodation for exercising troops and permanent staff, a joint officers' and sergeants' mess, a cookhouse, stores, vehicle repair facilities, a gymnasium, offices, and aviation facilities including a helipad and refuelling point. It remains a modest permanent presence in personnel terms but a significant seasonal one: thousands of British troops rotate through for exercises each year, making it, according to the British Army, one of its largest overseas training footprints in Africa.

Kenya's terrain offers vast, varied training grounds that support live-fire exercises, armoured manoeuvres, and combat simulations that are difficult to replicate in the UK. In exchange, Britain offers reciprocal training opportunities to the Kenya Defence Forces (KDF) in the UK, alongside joint exercises run from the Nanyuki base itself.

The economic footprint is also significant. Hotels, transport providers, food suppliers, construction firms, and small businesses in and around Nanyuki have long benefited from the steady flow of visiting troops, a fact Kenyan officials cite whenever the partnership comes under strain.

## The Agreement Between London and Nairobi and Why It's Under Strain

The current friction centres on the unratified Defence Cooperation Agreement and a parliamentary inquiry that has turned BATUK from a routine defence footnote into a live political issue.

In November 2025, the National Assembly's Departmental Committee on Defence, Intelligence and Foreign Relations tabled a report following its investigation into BATUK's conduct in Kenya. The findings included allegations of sexual abuse, including rape and assault; concerns about negligence in handling unexploded ordnance, which the committee linked to civilian injuries and deaths; allegations that Kenyan workers hired for debris clearance were sent in without adequate protective equipment; and concerns about environmental damage from improper waste and hazardous-material disposal.

The report also revisited one of the most sensitive unresolved cases tied to the base: the 2012 murder of Agnes Wanjiru, a Kenyan woman whose death has long been linked to a British soldier stationed at BATUK. In September 2025, Kenya's Director of Public Prosecutions determined that a British national should stand trial over her killing, a decision that has kept the case central to the committee's demands for accountability.

Off the back of the inquiry, MPs pushed for substantial changes before any new DCA is ratified: expanding Kenyan courts' jurisdiction over crimes committed by visiting forces; mandatory cooperation with Kenyan criminal investigations and parliamentary summonses; a clearer code of conduct for foreign troops incorporated into the Kenya Defence Forces Act; stronger environmental safeguards; and civilian oversight mechanisms. Tensions boiled over in mid-2025 when BATUK officials failed to appear before the Defence Committee, prompting committee chair Nelson Koech to warn that they could be declared a "hostile witness" and compelled to appear under summons.

By late July 2026, the unresolved legal status of the DCA nearly derailed the relationship entirely. The UK's Ministry of Defence cancelled a planned September 2026 exercise, "Exercise Haraka Storm", after failing to secure the necessary Kenyan licences, a move that triggered public speculation about political interference and even bribery demands. Koech pushed back on those claims, attributing the delay to unresolved questions of jurisdiction, immunity, and accountability rather than any backroom dealing. Within days, the two governments announced a resumption of the training, with Prime Cabinet Secretary Musalia Mudavadi framing the resolution as proof the two countries could work through disagreements via dialogue. The autumn exercise is set to be the last of five planned for 2026, with five more scheduled for 2027, assuming the underlying legal disputes do not resurface.

Civil society has not waited for Parliament to finish the job. In June 2026, a coalition of human rights activists, victims' families, and local leaders held a public meeting in Nanyuki and later petitioned Parliament, government agencies, BATUK, and the UK Ministry of Defence directly, demanding reforms before any new DCA is signed. The group, which includes Free Kenya Movement leader Bob Njagi, has centred its campaign explicitly on Kenyan sovereignty.

## The Sovereignty Test: BATUK and the Sessional Paper No. 1 of 2025

The timing matters. In January 2025, Kenya's Cabinet adopted Sessional Paper No. 1 of 2025 on the Foreign Policy of the Republic of Kenya, the country's first consolidated, legally anchored foreign policy document. It was unveiled by President William Ruto in December 2024 and approved by the National Assembly in April 2025. Prime Cabinet Secretary Mudavadi has described its purpose as shifting Kenya away from opaque, conventional diplomacy towards a citizen-centred, transparent, and accountable approach that puts Kenyan interests first.

The BATUK dispute has effectively become the first serious stress test of that doctrine. A foreign policy paper built around sovereignty and accountability is difficult to reconcile with a six-decade-old military arrangement operating under an expired treaty, credible allegations of unpunished abuse, and a foreign partner that did not appear before its own parliamentary summons. Lawmakers driving the BATUK inquiry have explicitly invoked this tension, arguing that a foreign force cannot be allowed to continue operating in Kenya without proper legal backing and that any renewed partnership must rest on equality and respect for Kenyan law, not just economic convenience or historic goodwill.

Whether the new DCA, expected to be renegotiated through 2026, ends up reflecting the Sessional Paper's stated principles, or whether strategic and economic considerations dilute them, will say a great deal about how much practical weight Kenya's new foreign policy framework actually carries. For now, BATUK remains open, training continues, and the deeper legal reckoning has simply been deferred rather than resolved.

Sources: UK Ministry of Defence / Defence Infrastructure Organisation (insidedio.blog.gov.uk); Eastleigh Voice; Pulse Kenya; Al Jazeera; Citizen Digital; Forces News; The Standard; Breaking Kenya News; The Weekly Vision Media; The Star; allAfrica; Nairobi Wire; Kenya Parliament Hansard records; Kenya Gazette; Ministry of Foreign Affairs Kenya; UK Ministry of Defence written statement to the Kenyan Parliamentary Inquiry into BATUK.`,
    authorBio: "Benjamin Matei Kitonga is an award-winning researcher, accomplished mooter, and peace and security enthusiast passionate about diplomacy, conflict resolution, and international affairs. He is pursuing a Master's degree in International Relations, with a concentration in Peace and Conflict Studies, at USIU-Africa. Recognized nationally and internationally for excellence in advocacy and alternative dispute resolution, Benjamin actively contributes to research, policy dialogue, and peacebuilding initiatives."
  },
  {
    section: "article",
    id: 1,
    title: "BRICS versus the West: Weighing the Economic and Political Dividends for Kenya",
    excerpt: "Kenya faces a critical foreign policy choice between its traditional Western allies and the rising BRICS alliance. This analysis weighs the economic and political dividends of each option for Kenya's national interests.",
    image: "/images/brics-west-article.png",
    category: "International Relations",
    date: "April 2026",
    author: "Jeremy Oronje",
    readTime: "10 min read",
    tags: ["BRICS", "Kenya", "Foreign Policy", "Geopolitics"],
    fullContent: "Following independence in 1963, Kenya adopted a foreign policy that, particularly in matters of security and economic cooperation, leaned towards the West. This orientation was shaped by its colonial ties to Britain, the pursuit of a capitalist economic model, and the strategic imperatives of the Cold War. During the post-independence period, Kenya was keen to establish diplomatic relations with several countries in a bid to assert its recently gained sovereignty. Under President Moi's leadership (1978-2000), Kenya retained the strong economic linkages with the West, particularly evidenced through aid and concessional loans from the Bretton Woods institutions.\n\nAfter the end of the Cold War, Western partners increasingly conditioned their support on governance reforms, democratization, and anti-corruption measures, a trigger that prompted the need to diversify partnerships. The Kibaki administration (2002-2012) adopted a foreign policy that sought to cultivate stronger economic ties with emerging powers such as China, Japan, and other Asian states in search of alternative financing. In 2013, President Uhuru Kenyatta maintained the same orientation towards the East, most notably securing financing for major infrastructure projects from China, while simultaneously balancing Kenya's security and trade partnerships with the West.\n\nUpon his assumption to office in 2022, President Ruto articulated Kenya's foreign policy stance by stating \"We are neither facing East nor West, we are facing forward\". During a visit by Chinese official Li Xi to Kenya in November 2024, President William Ruto formally expressed Kenya's interest in joining the Brazil-Russia-India-China-South Africa (BRICS) grouping. As of August 2025, no progress has been made towards the same and Kenya appears to have moderated its posture towards the alliance. This paper weighs the economic and political dividends between the West and the rising BRICS alliance.\n\nWhat does BRICS+ have to offer for Kenya? Originally BRIC, the term was coined by Jim O'Neill, an economist from Goldman Sachs in a 2001 research paper. The alliance was institutionalized in 2009, positioning itself as a platform advocating reforms in global governance and multilateral institutions. Given Kenya's current pressing national priorities, particularly the urgent need to address its debt burden, BRICS may not present substantial immediate benefits. Kenya's public debt currently stands at approximately USD 81.14 billion (KES 11.36 trillion), with nearly 67 per cent of the country's ordinary revenue directed towards debt servicing.\n\nThe New Development Bank (NDB), perceived as BRICS' strategic counterweight to the Bretton-Woods institutions, could be of advantage to Kenya for specific development projects. However, the NDB is 14 times smaller than the World Bank and 1.5 times smaller than the IMF in terms of asset value, underscoring its insufficient capacity to fund large scale projects. Politically, aligning with BRICS proves to be more costly for Kenya than advantageous. The Trump administration has signaled a warning to BRICS member states, threatening to impose 100 per cent tariffs should they proceed with de-dollarization plans, which would negatively impact key sectors such as horticulture, tea and apparel.\n\nWhat does the West have to offer? Over the years, Kenya has derived significant benefits from its cooperation with the West. In the last three years, Kenya has received a cumulative loan amount of USD 1.59 billion for its economic stabilization and development programs, as part of a broader commitment of USD 3.62 billion from the IMF. From 2022 to 2024, Kenya received USD 5.38 billion from the World Bank. From a security perspective, Kenya is currently in several partnerships in counterterrorism, maritime security, military training, and intelligence sharing with the US, UK and European Union. BRICS does not have any structured security framework, thus offering no security incentive for Kenya's membership.\n\nWhich Foreign Policy would be Ideal for Kenya? The foreign policy adopted by any country should be driven by its national interests. A policy that appeases Western allies while making new ones would be ideal. Kenya could leverage rising powers such as Qatar, Turkey, Indonesia, and UAE, all of which have shown an interest in expanding their influence in the continent. Kenya's external engagements could, in the short term, be premised on securing concessional financing but with the long term goal of securing agreements that will stimulate job creation and enhance industrial growth.\n\nAs it stands, the West is still a preferred ally over BRICS. Alliances are however very unpredictable regardless of how cohesive they may appear, hence the need to have an open foreign policy that avoids overdependence on a single bloc or ally.",
    authorBio: "Jeremy Oronje is a researcher, with over three years of experience in the field of international relations and policy research. His work focuses on armed conflict, geopolitics, and governance in the Horn of Africa and the Great Lakes region."
  },
  {
    section: "study",
    id: 2,
    title: "Political Participation Among University Students in Kenya",
    image: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800&h=500&fit=crop",
    category: "Youth & Governance",
    date: "October 2025",
    author: "IRSAK Research Team",
    readTime: "15 min read",
    tags: ["Youth Engagement", "Governance", "Political Participation"],
    fullContent: "This study examined political participation among university students across 28 Kenyan universities. Key findings reveal that while students demonstrate moderate political awareness of elected leaders and party manifestos, active participation remains low. Only 21% are registered voters, 12% voted in the last election, and just 5% are political party members. However, 42.9% participated in protests, particularly the anti-Finance Bill demonstrations. Major barriers include distrust of politicians (70% cite this), security concerns from police brutality and violence, and information inaccessibility. Nearly 80% believe politicians don't care about youth. Social media emerged as the primary information source (83%) and preferred platform for civic education (70%). The study recommends strengthening trust through intergenerational dialogues, IEBC engagement initiatives, integration of civic education into curricula, and deliberate youth inclusion in public participation processes.",
    pdfUrl: "/documents/Political_Participation_Study_IRSAK_2025.pdf"
  },
    {
    section: "article",
    id: 3,
    title: "Cracks in the CRINK: Illusions of an Autocratic Alliance",
    excerpt: "An in-depth analysis of the fragile coalition between China, Russia, Iran, and North Korea, examining how divergent agendas and internal contradictions undermine the notion of a unified autocratic axis.",
    image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&h=500&fit=crop",
    category: "International Relations",
    date: "January 2026",
    author: "Caroline Nyagaki",
    readTime: "10 min read",
    tags: ["CRINK", "Geopolitics", "International Relations"],
    fullContent: `In recent years, geopolitical analysts have increasingly referred to the emerging alignment between China, Russia, Iran, and North Korea as an "autocratic axis"\u2014a loose coalition bound by opposition to Western-led global governance. Popularly labeled as the CRINK bloc, this group is often portrayed as a rising challenge to the liberal international order, capable of undermining U.S. and European strategic influence. However, a closer examination reveals that the bloc is far from cohesive. Instead, it is characterized by fragility, transactional interests, and internal contradictions.

Despite appearances, CRINK is not a unified alliance. It lacks the institutional infrastructure, mutual defense agreements, or shared strategic vision found in established alliances like NATO. What binds these states is not long-term loyalty or ideological harmony, but a shared opposition to Western dominance, a desire for regime survival, and selective cooperation based on short-term advantage.

## A Bloc of Divergent Agendas

Though often grouped together as "anti-Western" actors, the CRINK countries differ significantly in both their regional ambitions and global outlooks. China envisions a restructured international order that reflects its growing global power but remains economically interdependent with the West. Stability, not chaos, is in Beijing's best interest. Russia, increasingly isolated after its 2022 invasion of Ukraine, now leans heavily on China for trade and political cover. Yet it continues to act aggressively and unpredictably, seeking to reclaim lost geopolitical influence. Iran walks a fine line between revolutionary ideology and pragmatic diplomacy. While it actively supports armed proxies and regional militancy, it also seeks economic relief and engagement with Europe and emerging powers. North Korea, perhaps the most unpredictable member, continues to pursue nuclear brinkmanship. Its leadership appears more focused on regime survival and domestic control than any form of coherent external alliance.

This lack of strategic unity undermines any serious attempt to view CRINK as a consolidated power bloc.

## China: Cautious Leader, Not Commander

China's role within CRINK is particularly revealing. It is the most powerful player\u2014economically, diplomatically, and increasingly militarily. Yet Beijing has consistently avoided taking on a leadership role within the group. For instance, although China has provided economic support to Russia amid Western sanctions, it has avoided direct military assistance to avoid triggering secondary sanctions. Similarly, while it maintains close energy and infrastructure ties with Iran, it has not defended Iran's nuclear ambitions on the world stage. As for North Korea, China regards it more as a strategic liability than an asset, frequently urging restraint after provocative missile tests.

Rather than building an alliance based on loyalty, China appears to be managing a series of fragile dependencies, careful not to overcommit to any partner.

## No Real Alliance, Just Shared Convenience

Unlike structured alliances that commit to mutual defense and long-term strategy, CRINK operates more like a network of convenience. There is no CRINK summit, no joint military command, no institutional mechanism for resolving disputes or planning long-term collaboration. Their cooperation is issue-specific and driven by necessity: Russia turns to Iran for drones, North Korea supplies artillery shells to Russian forces in Ukraine, China trades with Russia and Iran to secure cheap energy, and Iran offers military and intelligence support to other authoritarian regimes.

But this does not equate to trust. In fact, mutual suspicion and asymmetry define these relationships. Russia's growing dependence on Chinese technology and trade has led to fears of strategic subordination. Iran, meanwhile, is wary of China's increasing influence over its economy. North Korea continues to act independently, often ignoring Beijing's calls for restraint.

## Implications for Global Stability

The illusion of unity within CRINK has two important implications for international affairs. First, it shows that Western fears of a coordinated "axis of autocracy" may be overstated. The bloc's internal divisions and lack of strategic coherence limit its ability to act as a united front. This presents an opportunity for diplomatic engagement and wedge strategies that target these divisions.

Second, CRINK's informal cooperation, while fragmented, still poses a challenge. Through asymmetric tactics\u2014such as cyberattacks, sanctions evasion, arms transfers, and disinformation campaigns\u2014these states can still undermine global norms and distract Western powers by igniting crises on multiple fronts simultaneously. The West, and indeed the Global South, must be alert not just to the actions of CRINK states, but to the conditions that enable them to act: instability, broken diplomacy, and economic fragmentation.

## Conclusion: Fragile Alliances, Not Fatal Threats

The CRINK bloc is more a geopolitical mirage than a durable axis. What unites China, Russia, Iran, and North Korea is not a grand design, but shared grievance and opportunism. As such, it should not be dismissed\u2014but neither should it be overestimated. For international relations scholars and emerging policy thinkers, including members of IRSAK, this case highlights the importance of examining alliances beyond surface-level narratives. Real power blocs are built on trust, coordination, and shared values\u2014not just mutual enemies.`

  },
  {
    section: "brief",
    id: 4,
    title: "Policy Brief 001: Strengthening Youth Participation in Politics and Governance in Kenya",
    excerpt: "This policy brief presents actionable recommendations drawn from a roundtable discussion convened by YIGA in February 2026, building on key findings from a research study on political participation among university students in Kenya.",
    image: "/images/activities/Roundtable_on_strengthening_Youth_Participation_in_Politics_and_Governance.jpeg",
    category: "Policy Brief",
    date: "May 2026",
    author: "YIGA",
    readTime: "5 min read",
    tags: ["Policy Brief", "Youth", "Governance", "Kenya"],
    pdfUrl: "/documents/YIGA_Policy_Brief_001.pdf",
    fullContent: "This policy brief presents actionable recommendations drawn from a roundtable discussion convened by YIGA on 6th February 2026, pegged on strengthening youth participation in politics and governance. The event was attended by leading civil society organizations, researchers, university student leaders, and representatives from the National Youth Council and the Centre for Multiparty Democracy Kenya.\n\nKey Finding 1: Lack of Trust in Politicians and Political Institutions. Nearly 70% of respondents identified distrust in political leaders as a major reason for their limited engagement in formal political processes. An alarming 80% indicated that politicians do not care about the youth.\n\nKey Finding 2: A Small Fraction of the Demographic is Disinterested in Politics. 20% of respondents had no interest in politics and governance, negatively impacting their political awareness and participation.\n\nKey Finding 3: Limited Information on Political Activities Hinders Participation. Most respondents did not know where to get credible political information on public consultation processes and political party manifestos.\n\nKey Finding 4: Social Media Plays a Significant Role. 83% of students reported relying on social media as their primary source of information, with 70% preferring it for civic education initiatives.\n\nThe policy brief recommends that Political Parties clearly align their legislation to their manifestos, organize virtual forums, and communicate the benefits of party membership. The IEBC should better resource its youth committee and organize open house forums on electoral processes. Civil Society Organizations should champion joint civic education initiatives and collaborate on information dissemination strategies."
  },
  {
    section: "brief",
    id: 6,
    title: "Kenya's Peace and Security Diplomacy: Gains and Gaps",
    excerpt: "Assessing Sessional Paper No. 1 of 2025 against Kenya's actual mediation and peacekeeping record in Sudan, South Sudan, the eastern DRC, Somalia and Haiti. Drawn from a youth dialogue jointly convened by YIGA, Nala Center and MuemAction Post at the Konrad Adenauer Stiftung Kenya office.",
    image: "/images/activities/Peace_and_Security_Brief_Cover.svg",
    category: "Policy Brief",
    date: "August 2026",
    author: "YIGA, Nala Center & MuemAction Post",
    readTime: "6 min read",
    tags: ["Peace and Security", "Kenya", "Foreign Policy", "Diplomacy"],
    pdfUrl: "/documents/YIGA_Kenya_Peace_and_Security_Diplomacy_Policy_Brief.pdf",
    fullContent: "This policy brief is informed by the proceedings of a youth dialogue on Kenya's peace and security diplomacy, convened on 14 August 2026 at the Konrad Adenauer Stiftung Kenya office in Nairobi. The dialogue was jointly organised by Youth in Governance and Global Affairs, Nala Center and MuemAction Post, and examined the peace and security commitments of Sessional Paper No. 1 of 2025 against Kenya's recent conduct in Sudan, South Sudan, the eastern DRC, Somalia and Haiti. Around 50 participants took part. Ambassador Haile Menkerios delivered the keynote address, Dr Nicodemus Minde and Major (Rtd) Beautah Mwanza Suba formed the panel, and the session was moderated by Aisha Ibrahim.\n\nThe consensus of the dialogue is that Kenya's difficulty in peace and security diplomacy is not a shortage of assets but a shortage of institutional machinery to convert them. Measured against a three-level test of diplomatic activity, mediation effectiveness and conflict transformation, Kenya performs strongly on activity, unevenly on effectiveness and weakly on durable transformation.\n\nKey Finding: Diplomatic Activity Is Not Mediation Effectiveness. Kenya's record is strongest at the level of meetings, envoys and communiques, and becomes progressively weaker as the analysis moves toward implementation and transformation.\n\nKey Finding: Mediator Credibility Is a Finite Strategic Asset. Sudan made credibility rather than access the central question, with the Nairobi meetings conducted without an IGAD or African Union mandate.\n\nKey Finding: Fragmented Institutional Memory Is the Binding Constraint. Kenya's constraint is not capability, funding or courage, but the absence of a systematic mechanism for capturing and sharing what its diplomats and peacekeepers have learned.\n\nKey Finding: Peacekeeping Is Not Being Converted into National Dividend, and Peacebuilding and Youth Participation Are Under-resourced, with no national action plan yet in place for UN Security Council Resolution 2250 on Youth, Peace and Security.\n\nKey Finding: Domestic Stability Is the Precondition for Regional Credibility, with Kenya's mediation legitimacy resting on the same domestic restraint and institutional capacity it seeks to promote abroad.\n\nThe brief recommends that Kenya establish a permanent mediation support unit, publish transparent criteria for intervention, create a national peace operations lessons repository, attach an economic diplomacy plan to every major deployment, integrate critical civilian infrastructure into national defence architecture, and develop and resource a national action plan on Resolution 2250 ahead of the next electoral cycle.\n\nDownload the full policy brief below for the complete findings and recommendations."
  }
];


// Past Activities Data - REPLACE WITH REAL DATA TOMORROW
const pastActivities = [
  {
    id: 1,
    title: "Roundtable on Strengthening Youth Participation in Politics and Governance",
    date: "February 2026",
    location: "Nairobi, Kenya",
    description: "YIGA hosted a roundtable that brought together representatives from civil society, political party youth leagues, government institutions, students, and researchers to discuss strategies for strengthening youth participation in politics and governance.",
    image: "/images/activities/Roundtable_on_strengthening_Youth_Participation_in_Politics_and_Governance.jpeg",
    category: "Roundtable",
    outcomes: ["Policy brief with actionable recommendations", "Strengthened multi-stakeholder collaboration on youth participation in governance"]
  },
  {
    id: 2,
    title: "Research Study Launch: Political Participation Among University Students in Kenya",
    date: "October 2025",
    location: "Nairobi, Kenya",
    description: "YIGA organized a launch event to officially unveil its study titled Political Participation Among University Students in Kenya. The launch marked the culmination of a six-month research project.",
    image: "/images/activities/Research_Study_Launch.jpeg",
    category: "Research Launch",
    outcomes: ["Publication of the research study report", "Dissemination of findings to policymakers, civil society, and youth stakeholders"]
  },
  {
    id: 3,
    title: "Official Visit to the United Nations Development Programme (UNDP)",
    date: "September 2025",
    location: "Kigali, Rwanda",
    description: "YIGA, in collaboration with the United Nations Development Programme Rwanda Country Office, held an engagement session exploring the role of youth during and after the Rwandan Genocide.",
    image: "/images/activities/Official_Visit_to_UNDP.jpeg",
    category: "International Engagement",
    outcomes: ["Enhanced understanding of youth-led peacebuilding initiatives", "Lessons on post-conflict reconciliation and community rebuilding"]
  },
  {
    id: 4,
    title: "Official Visit to Rwanda Cooperation",
    date: "September 2025",
    location: "Kigali, Rwanda",
    description: "YIGA met with senior officials from Rwanda Cooperation to discuss governance practices and institutional development.",
    image: "/images/activities/Official_Visit_To_Rwanda_Cooperation.jpeg",
    category: "Institutional Visit",
    outcomes: ["Knowledge exchange on governance and institutional reforms", "Strengthened networks for future collaboration"]
  },
  {
    id: 5,
    title: "Roundtable on the Role of Youth in Peacebuilding",
    date: "September 2025",
    location: "Kigali, Rwanda",
    description: "YIGA visited Never Again Rwanda, a youth-led grassroots organization, for a dialogue on the role of young people in promoting peace, reconciliation, and social cohesion in post-conflict societies.",
    image: "/images/activities/Rountable_on_the_role_of_Youth_in_Peace_Building.jpeg",
    category: "Peacebuilding",
    outcomes: ["Insights into grassroots peacebuilding initiatives", "Strengthened partnerships with youth-led peacebuilding organizations"]
  },
  {
    id: 6,
    title: "United Nations Day for South-South Cooperation",
    date: "September 2025",
    location: "Kigali, Rwanda",
    description: "YIGA representatives participated in activities commemorating the United Nations Day for South-South Cooperation.",
    image: "/images/activities/UN_day_for_South-South_Cooperation.jpeg",
    category: "UN Event",
    outcomes: ["Engagement with diplomatic and policy stakeholders", "Increased awareness of South-South cooperation in development and governance"]
  },
  {
    id: 7,
    title: "Career and Mentorship Forum",
    date: "March 2025",
    location: "Nairobi, Kenya",
    description: "Through its student partner International Relations Students Association of Kenya (IRSAK), YIGA partnered with Konrad Adenauer Stiftung and the Catholic University of Eastern Africa to organize a career and mentorship forum for students of international relations.",
    image: "/images/activities/career_and_mentorship_forum.jpeg",
    category: "Mentorship",
    outcomes: ["Career mentorship and professional exposure for students", "Strengthened networks between students and practitioners in international affairs"]
  },
  {
    id: 8,
    title: "Institutional Visit to Regional Organizations",
    date: "October 2023",
    location: "Arusha, Tanzania",
    description: "YIGA, through its student partner International Relations Students Association of Kenya and the Catholic University of Eastern Africa, organized an educational visit to the East African Community and the African Court on Human and Peoples' Rights.",
    image: "/images/activities/East_African_Community_visit.jpeg",
    category: "Educational Visit",
    outcomes: ["Enhanced student understanding of regional governance institutions", "Practical exposure to regional diplomacy and legal processes"]
  }



























































];

// Real YIGA Articles with full content
const newsletterArchives = [
  {
    id: 1,
    title: "November 2025 - Youth Voices in Climate Action",
    date: "November 30, 2025",
    excerpt: "This month's highlights on youth-led climate initiatives across Africa"
  },
  {
    id: 2,
    title: "October 2025 - Peace and Security Roundup",
    date: "October 31, 2025",
    excerpt: "Key developments in African peace processes and youth involvement"
  },
  {
    id: 3,
    title: "September 2025 - Governance Reforms Update",
    date: "September 30, 2025",
    excerpt: "Latest insights on democratic reforms and youth participation"
  }
];


const AdvisorCard = ({ advisor }) => {
  const [showBio, setShowBio] = React.useState(false);
  return (
    <div className="text-center bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300">
      <img
        src={advisor.photo}
        alt={advisor.name}
        className="w-32 h-32 rounded-full object-cover border-4 border-black shadow-lg mx-auto mb-4 cursor-pointer hover:border-red-600 transition duration-300"
        onClick={() => setShowBio(!showBio)}
      />
      <h4 className="text-lg font-bold text-black mb-1">{advisor.name}</h4>
      <p className="text-gray-700 font-semibold text-sm mb-3">Board Advisor</p>
      <div className="w-8 h-0.5 bg-black mx-auto mb-3"></div>
      {showBio
        ? <p className="text-gray-500 text-sm leading-relaxed">{advisor.bio}</p>
        : <p className="text-gray-400 text-xs italic cursor-pointer" onClick={() => setShowBio(true)}>Click photo to view bio</p>
      }
    </div>
  );
};
function App() {
  useEffect(() => { emailjs.init('pcctBpi8SrH0A5b6X'); }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Handle URL parameters for direct page access
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');
    if (page) {
      setCurrentPage(page);
    }
    const articleId = urlParams.get('article');
    if (articleId) {
      const found = articles.find(a => a.id === parseInt(articleId));
      if (found) {
        setSelectedArticle(found);
        setCurrentPage('research');
      }
    }
  }, []);

  const openArticle = (article) => {
    setSelectedArticle(article);
    const url = new URL(window.location);
    url.searchParams.set('article', article.id);
    window.history.pushState({}, '', url);
  };

  const closeArticle = () => {
    setSelectedArticle(null);
    const url = new URL(window.location);
    url.searchParams.delete('article');
    window.history.pushState({}, '', url);
  };
    const [currentPage, setCurrentPage] = useState('home');
  const [expandedSection, setExpandedSection] = useState('');
  const [teamView, setTeamView] = useState('directorate');
  const [secretariatDept, setSecretariatDept] = useState('Finance');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [articleView, setArticleView] = useState('research');
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState('');
  const [applications, setApplications] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [newsletterPreferences, setNewsletterPreferences] = useState({
    email: '',
    frequency: 'monthly',
    topics: []
  });

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    institution: '',
    program: 'General Membership',
    motivation: '',
    experience: ''
  });

  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  const [newAdmin, setNewAdmin] = useState({
    username: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    if (isLoggedIn && currentPage === 'admin') {
      fetchApplications();
      fetchNewsletterSubscribers();
      if (userRole === 'superadmin') {
        fetchAdmins();
      }
    }
  }, [isLoggedIn, currentPage, userRole]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    
    try {
      // TEMPORARY: Hardcoded admin access
      if (loginData.username === 'Shawn' && loginData.password === 'Yiga2023') {
        setIsLoggedIn(true);
        setUsername('Shawn');
        setUserRole('superadmin');
        localStorage.setItem('username', 'Shawn');
        localStorage.setItem('userRole', 'superadmin');
        setLoginData({ username: '', password: '' });
        await fetchApplications();
        if (userRole === 'superadmin' || true) await fetchAdmins();
        setLoading(false);
        return;
      }






      const { data: admins, error } = await supabase
        .from('admins')
        .select('*')
        .eq('username', loginData.username)
        .single();

      if (error || !admins) {
        setLoginError('Invalid username or password');
        setLoading(false);
        return;
      }

      // Verify password
      const passwordMatch = await bcrypt.compare(loginData.password, admins.password);
      
      if (passwordMatch) {
        setIsLoggedIn(true);
        setUsername(admins.username);
        setUserRole(admins.role);
        localStorage.setItem('username', admins.username);
        localStorage.setItem('userRole', admins.role);
        setLoginData({ username: '', password: '' });
        await fetchApplications();
        if (admins.role === 'superadmin') await fetchAdmins();
      } else {
        setLoginError('Invalid username or password');
      }
    } catch (error) {
      setLoginError('Login error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
































































  const handleLogout = () => {
    setAuthToken('');
    setIsLoggedIn(false);
    setUsername('');
    setUserRole('');
    setCurrentPage('home');
  };

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };


















  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);

  const fetchNewsletterSubscribers = async () => {
    try {
      const { data, error } = await supabase.from('newsletter_subscribers').select('*').eq('is_active', true).order('subscribed_at', { ascending: false });
      if (error) throw error;
      setNewsletterSubscribers(data || []);
    } catch (error) {
      console.error('Error fetching newsletter subscribers:', error);
    }
  };

  const sendNewsletter = async () => {
    const subject = prompt('Enter newsletter subject:');
    const message = prompt('Enter newsletter message:');
    
    if (!subject || !message) return;
    
    try {
      for (const subscriber of newsletterSubscribers) {
        await emailjs.send('service_rkhhb36', 'template_ptkd39e', {
          to_email: subscriber.email,
          email_subject: subject,
          email_heading: subject,
          email_body: message,
          has_button: false
        });
      }
      alert(`Newsletter sent to ${newsletterSubscribers.length} subscribers!`);
    } catch (error) {
      console.error('Error sending newsletter:', error);
      alert('Failed to send newsletter');
    }
  };

  const deleteNewsletterSubscriber = async (id) => {
    try {
      const { error } = await supabase.from('newsletter_subscribers').delete().eq('id', id);
      if (error) throw error;
      await fetchNewsletterSubscribers();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase.from('admins').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };
















  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    try {
      const { error } = await supabase.from('applications').insert([{
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        country: formData.institution || formData.country,
        motivation: formData.motivation,
        status: 'pending'
      }]);
      if (error) throw error;
      emailjs.send("service_rkhhb36", "template_ptkd39e", {
        to_email: formData.email,
        email_subject: "We received your application",
        email_heading: "Thanks for applying, " + formData.full_name + "!",
        email_body: "We have received your YIGA membership application and our team will review it shortly. You will receive another email once a decision has been made.",
        has_button: false
      });

      
      setFormData({ full_name: '', email: '', phone: '', country: '', city: '', institution: '', program: 'General Membership', motivation: '', experience: '' });
      setSubmitStatus('success');
      setTimeout(() => setSubmitStatus(''), 5000);
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 5000);
    }
  };

















































































  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    try {
      const { error } = await supabase.from('newsletter_subscribers').insert([{
        email: newsletterEmail,
        frequency: 'monthly',
        topics: [],
        is_active: true
      }]);
      if (error) throw error;
      emailjs.send("service_rkhhb36", "template_ptkd39e", {
        to_email: newsletterEmail,
        email_subject: "You are subscribed to the YIGA Newsletter",
        email_heading: "You are in!",
        email_body: "Thanks for subscribing to the YIGA Newsletter. You will now receive our regional updates straight to your inbox.",
        has_button: false
      });
      setNewsletterStatus('success');
      setNewsletterEmail('');
      setTimeout(() => setNewsletterStatus(''), 5000);
    } catch (error) {
      if (error.code === "23505") {
        setNewsletterStatus("success");
        setNewsletterEmail("");
        setTimeout(() => setNewsletterStatus(""), 5000);
      } else {
        console.error("Newsletter error:", error);
        setNewsletterStatus("error");
        setTimeout(() => setNewsletterStatus(""), 5000);
      }
    }
  };


















  const handleNewsletterPreferencesSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    try {
      const { error } = await supabase.from('newsletter_subscribers').insert([{
        email: newsletterPreferences.email,
        frequency: newsletterPreferences.frequency,
        topics: newsletterPreferences.topics,
        is_active: true
      }]);
      if (error) throw error;
      setNewsletterStatus('success');
      setNewsletterPreferences({ email: '', frequency: 'monthly', topics: [] });
      setTimeout(() => setNewsletterStatus(''), 5000);
    } catch (error) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus(''), 5000);
    }
  };






















































  const updateApplicationStatus = async (id, status) => {
    try {
      const { error } = await supabase.from('applications').update({ status }).eq('id', id);
      if (error) throw error;
      if (status === 'approved') {
        const app = applications.find(a => a.id === id);
        await emailjs.send('service_rkhhb36', 'template_o2qqnzc', { email: app.email, name: app.full_name, pdfLink: 'https://yiga-website.vercel.app/documents/YIGA_Membership_Benefits.pdf', formLink: 'https://docs.google.com/forms/d/e/1FAIpQLSfzgMh6b4qCYeZfwluAsPd7XRecmM-ejw6wmNfYmkB7jIc6kg/viewform' });
      }
      await fetchApplications();
    } catch (error) {
      console.error('Error updating application:', error);
      alert('Failed to update status');
    }
  };


















  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      const { error } = await supabase.from('applications').delete().eq('id', id);
      if (error) throw error;
      await fetchApplications();
      alert('Application deleted successfully!');
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Failed to delete application');
    }
  };

















  const createAdmin = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminSuccess('');
    try {
      const hashedPassword = await bcrypt.hash(newAdmin.password, 10);
      const { error } = await supabase.from('admins').insert([{
        username: newAdmin.username,
        password: hashedPassword,
        role: newAdmin.role || 'admin'
      }]);
      if (error) throw error;
      setAdminSuccess('Admin created successfully!');
      setNewAdmin({ username: '', password: '', role: 'admin' });
      await fetchAdmins();
      setTimeout(() => setAdminSuccess(''), 3000);
    } catch (error) {
      console.error('Error creating admin:', error);
      setAdminError('Failed to create admin: ' + error.message);
    }
  };



























  const updateAdminRole = async (id, role) => {
    try {
      const { error } = await supabase.from('admins').update({ role }).eq('id', id);
      if (error) throw error;
      await fetchAdmins();
    } catch (error) {
      console.error('Error updating admin role:', error);
      alert('Failed to update role');
    }
  };


















  const deleteAdmin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    const admins_list = admins.find(a => a.id === id);
    if (admins_list && admins_list.username === "Shawn") { alert("This account cannot be deleted."); return; }
    try {
      const { error } = await supabase.from("admins").delete().eq("id", id);
      if (error) throw error;
      await fetchAdmins();
    } catch (error) {
      console.error("Error deleting admin:", error);
      alert("Failed to delete admin");
    }
  };















  const toggleNewsletterTopic = (topic) => {
    setNewsletterPreferences(prev => ({
      ...prev,
      topics: prev.topics.includes(topic)
        ? prev.topics.filter(t => t !== topic)
        : [...prev.topics, topic]
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-red-600 text-white py-2 px-4 text-center text-sm font-semibold">
        Join Africa's Leading Youth in International Affairs
      </div>

      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center space-x-3">
              <img src="/images/logo.jpeg" alt="YIGA Logo" className="h-16 w-auto object-contain" />




            </div>

            <div className="hidden md:flex items-center space-x-1">
              {['home', 'about', 'team', 'research', 'activities', 'programs', 'partners', 'join'].map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    currentPage === page
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                  }`}
                >
                  {page === 'partners' ? 'Student Partners' : page.charAt(0).toUpperCase() + page.slice(1)}
                </button>
              ))}
              {isLoggedIn && (
                <>
                  <button
                    onClick={() => setCurrentPage('admin')}
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      currentPage === 'admin'
                        ? 'text-red-600 bg-red-50'
                        : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                    }`}
                  >
                    Admin
                  </button>
                  <button
                    onClick={handleLogout}
                    className="ml-4 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center space-x-2 transition"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </>
              )}
            </div>

            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-red-600"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['home', 'about', 'team', 'research', 'activities', 'programs', 'partners', 'join'].map((page) => (
                <button
                  key={page}
                  onClick={() => {
                    setCurrentPage(page);
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-lg font-semibold ${
                    currentPage === page
                      ? 'bg-red-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {page === 'partners' ? 'Student Partners' : page.charAt(0).toUpperCase() + page.slice(1)}
                </button>
              ))}
              {isLoggedIn && (
                <>
                  <button
                    onClick={() => {
                      setCurrentPage('admin');
                      setMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 rounded-lg font-semibold ${
                      currentPage === 'admin'
                        ? 'bg-red-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Admin
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 rounded-lg bg-black text-white hover:bg-gray-800"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <div>
        {currentPage === 'home' && (
          <div>
            <div className="relative h-[600px] bg-gradient-to-r from-black via-gray-900 to-red-900 overflow-hidden">
              <div 
                className="absolute inset-0 opacity-40"
                style={{
                  backgroundImage: `url(${teamPhotos.heroImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              ></div>
              <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
                <div className="max-w-3xl text-white">
                  <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">

                  </h1>
                  <p className="text-xl md:text-2xl mb-8 text-gray-200">
                    Empowering young professionals to drive meaningful change in international relations, governance, and policy across the Eastern Africa and beyond
                  </p>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setCurrentPage('join')}
                      className="bg-red-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-red-700 transition shadow-xl flex items-center space-x-2"
                    >
                      <span>Join YIGA</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage('about')}
                      className="bg-white text-black px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition shadow-xl"
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 text-white py-12">
              <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-red-500 mb-2">300+</div>
                  <div className="text-gray-300">Active Members</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-red-500 mb-2">9+</div>
                  <div className="text-gray-300">Countries</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-red-500 mb-2">15</div>
                  <div className="text-gray-300">Events Hosted</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-red-500 mb-2">10+</div>
                  <div className="text-gray-300">Partner Organizations</div>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-16">
              <div className="mb-12">
                <h2 className="text-4xl font-bold text-black mb-4">Latest Insights</h2>
                <p className="text-lg text-gray-600">Exploring critical issues in African international relations and governance</p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {articles && articles.map((article) => (
                  <div key={article.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition group">
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {article.date}
                        </span>
                        <span>&bull; {article.readTime}</span>
                      </div>
                      <h3 className="text-xl font-bold text-black mb-3 group-hover:text-red-600 transition">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{article.excerpt}</p>
                      <button onClick={() => openArticle(article)} className="text-red-600 font-semibold flex items-center space-x-1 hover:space-x-2 transition-all cursor-pointer">
                        <span>Read More</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-600 to-black text-white py-16">
              <div className="max-w-4xl mx-auto px-4 text-center">
                <Mail className="w-16 h-16 mx-auto mb-6" />
                <h2 className="text-4xl font-bold mb-4">Subscribe to Our Newsletter</h2>
                <p className="text-xl mb-8 text-gray-200">
                  Stay updated with the latest insights, events, and opportunities in African international affairs
                </p>
                <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
                  <div className="flex gap-3">
                    <input
                      type="email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-6 py-4 rounded-lg text-black focus:ring-2 focus:ring-white"
                      required
                    />
                    <button
                      type="submit"
                      disabled={newsletterStatus === 'submitting'}
                      className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition shadow-2xl disabled:bg-gray-300"
                    >
                      {newsletterStatus === 'submitting' ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </div>
                  {newsletterStatus === 'success' && (
                    <div className="mt-4 bg-green-100 text-green-800 p-3 rounded-lg font-bold">
                    Successfully subscribed!
                    </div>
                  )}
                  {newsletterStatus === 'error' && (
                    <div className="mt-4 bg-red-100 text-red-800 p-3 rounded-lg font-bold">
                      ÃƒÂ¢Ã‚ÂÃ…â€™ Subscription failed. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-600 to-black text-white py-20">
              <div className="max-w-4xl mx-auto px-4 text-center">
                <h2 className="text-4xl font-bold mb-6">Ready to Make an Impact?</h2>
                <p className="text-xl mb-8 text-gray-200">
                  Join a network of passionate young professionals shaping Africa's future in international affairs
                </p>
                <button
                  onClick={() => setCurrentPage('join')}
                  className="bg-white text-red-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition shadow-2xl"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        )}

        {currentPage === 'about' && (
          <div>
            <div className="relative h-96 bg-gradient-to-r from-black to-red-900">
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: `url(${teamPhotos.aboutImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              ></div>
              <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
                <div className="text-white max-w-3xl">
                  <h1 className="text-5xl font-bold mb-4">About YIGA</h1>
                  <p className="text-xl text-gray-200">Empowering Africa's next generation of global leaders</p>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-16">
              <div className="mb-16">
                <h3 className="text-3xl font-bold text-black mb-6 text-center">Who We Are</h3>
                <p className="text-gray-700 text-lg leading-relaxed max-w-4xl mx-auto text-center">
                  The Youth in Governance and Global Affairs (YIGA) is a youth-led civil society organization dedicated to advancing meaningful youth engagement in democratic participation, peace and security, foreign policy, and environmental sustainability across Eastern Africa. YIGA works to empower young people to actively participate in shaping policy and public discourse through capacity-building trainings, high-level workshops, and structured dialogue platforms. Grounded in a strong research-driven approach, the organization generates knowledge, fosters critical thinking, and equips youth with the skills necessary to contribute to local, regional, and global decision-making processes.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-12 mb-16">
                <div className="bg-white p-8 rounded-lg shadow-xl border-l-4 border-red-600">
                  <h3 className="text-3xl font-bold mb-4 text-black">Our Vision</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    To be a leading platform for youth leadership and engagement in shaping global discourse and development.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-xl border-l-4 border-black">
                  <h3 className="text-3xl font-bold mb-4 text-black">Our Mission</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    To empower young people through research, dialogue, and strategic engagement in key global issues, fostering informed, innovative, and impactful youth participation in international affairs.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-12 rounded-lg mb-16">
                <h3 className="text-3xl font-bold text-black mb-8 text-center">Core Objectives</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {[
                    "Promote and support the production of high-quality, youth-led research on pressing governance and global challenges",
                    "Deliver trainings, workshops, and conferences that equip young people with the knowledge and skills to engage effectively in governance and global affairs",
                    "Advance meaningful youth participation in foreign policy discourse and international relations processes",
                    "Foster inclusive spaces for dialogue, knowledge exchange, and collaboration among youth and policy stakeholders"
                  ].map((objective, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-700 text-lg">{objective}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-12 rounded-lg shadow-xl">
                <h3 className="text-3xl font-bold text-black mb-8 text-center">Focus Areas</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { name: "Foreign Policy", icon: Globe },
                    { name: "Governance & Democratic Participation", icon: Scale },
                    { name: "Climate Change & Environmental Sustainability", icon: Award },
                    { name: "Peace & Security", icon: Shield }

                  ].map((area, idx) => (
                    <div key={idx} className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition">
                      <area.icon className="w-12 h-12 mx-auto text-red-600 mb-3" />
                      <h4 className="font-bold text-lg text-black">{area.name}</h4>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentPage === 'team' && (
          <div className="min-h-screen bg-gray-50">

            {/* Hero Banner */}
            <div className="bg-gradient-to-r from-black to-red-900 text-white py-20">
              <div className="max-w-6xl mx-auto px-4 text-center">
                <h1 className="text-5xl font-bold mb-4">Meet Our Team</h1>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">The passionate individuals driving YIGA mission of youth empowerment in global governance and international affairs</p>
              </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-16">

              {/* Team Category Tabs */}
              <div className="grid md:grid-cols-3 gap-4 mb-12">
                <button
                  onClick={() => setTeamView('directorate')}
                  className={`flex items-center justify-center gap-3 px-6 py-6 rounded-xl font-bold text-lg shadow-lg transition ${teamView === 'directorate' ? 'bg-red-600 text-white' : 'bg-white text-black hover:shadow-xl'}`}
                >
                  <Users className="w-6 h-6" />
                  Secretariat
                </button>
                <button
                  onClick={() => setTeamView('advisors')}
                  className={`flex items-center justify-center gap-3 px-6 py-6 rounded-xl font-bold text-lg shadow-lg transition ${teamView === 'advisors' ? 'bg-black text-white' : 'bg-white text-black hover:shadow-xl'}`}
                >
                  <Award className="w-6 h-6" />
                  Board of Advisors
                </button>
                <button
                  onClick={() => setTeamView('trustees')}
                  className={`flex items-center justify-center gap-3 px-6 py-6 rounded-xl font-bold text-lg shadow-lg transition ${teamView === 'trustees' ? 'bg-black text-white' : 'bg-white text-black hover:shadow-xl'}`}
                >
                  <Shield className="w-6 h-6" />
                  Board of Trustees
                </button>
              </div>

              {teamView === 'directorate' && (
                <div className="bg-white p-8 rounded-xl shadow-xl mb-6">
                  {/* Leadership - always visible */}
                  <div className="grid md:grid-cols-2 gap-8 mb-10 pb-10 border-b border-gray-200">
                    {teamMembers.filter(m => m.department === 'Leadership').map((member, index) => (
                      <div key={index} className="group text-center bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <div className="relative inline-block mb-4">
                          <img src={member.photo} alt={member.name} className="w-32 h-32 rounded-full object-cover border-4 border-red-600 shadow-lg group-hover:border-black transition duration-300" />
                          <div className="absolute inset-0 rounded-full bg-red-600 opacity-0 group-hover:opacity-10 transition duration-300"></div>
                        </div>
                        <h4 className="text-lg font-bold text-black mb-1">{member.name}</h4>
                        <p className="text-red-600 font-semibold text-sm mb-3">{member.role}</p>
                        <div className="w-8 h-0.5 bg-red-600 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">{member.bio}</p>
                      </div>
                    ))}
                  </div>

                  {/* Department Sub-Tabs */}
                  <div className="flex flex-wrap gap-3 mb-8 justify-center">
                    {['Research', 'Programs and Partnerships', 'Events', 'Finance', 'Communication'].map((dept) => (
                      <button
                        key={dept}
                        onClick={() => setSecretariatDept(dept)}
                        className={`px-5 py-2 rounded-full font-semibold text-sm transition ${secretariatDept === dept ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {dept}
                      </button>
                    ))}
                  </div>

                  <div className="grid md:grid-cols-3 gap-8">
                    {teamMembers.filter(m => m.department === secretariatDept).map((member, index) => (
                      <div key={index} className="group text-center bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <div className="relative inline-block mb-4">
                          <img src={member.photo} alt={member.name} className="w-32 h-32 rounded-full object-cover border-4 border-red-600 shadow-lg group-hover:border-black transition duration-300" />
                          <div className="absolute inset-0 rounded-full bg-red-600 opacity-0 group-hover:opacity-10 transition duration-300"></div>
                        </div>
                        <h4 className="text-lg font-bold text-black mb-1">{member.name}</h4>
                        <p className="text-red-600 font-semibold text-sm mb-3">{member.role}</p>
                        <div className="w-8 h-0.5 bg-red-600 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">{member.bio}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {teamView === 'advisors' && (
                <div className="bg-white p-8 rounded-xl shadow-xl mb-6">
                  <div className="grid md:grid-cols-3 gap-8">
                    {[
                      { name: "Ambassador Haile Menkerios", photo: "/images/Ambassador_Haile_Menkerios.png", bio: "Bio coming soon" },
                      { name: "Richard Kakeeto", photo: "/images/Richard_Kakeeto.jpeg", bio: "Bio coming soon" },
                      { name: "Dr Nicodemus Minde", photo: "/images/Dr_Nicodemus_Minde.jpeg", bio: "Bio coming soon" },
                      { name: "Amb. Peter Katana Angore", photo: "/images/Ambassador Peter Katana Angore.jpeg", bio: "Former Kenyan Ambassador to Algeria (2019-2024) with 34+ years in the Ministry of Foreign Affairs. Led the technical team negotiating the South Sudan peace agreement (R-ARCSS 2018), awarded the Order of the Golden Warrior (OGW). Served in Khartoum, Pretoria, Juba and Brasilia. MA in International Studies, University of Nairobi." }
                    ].map((advisor, idx) => (
                      <AdvisorCard key={idx} advisor={advisor} />
                    ))}
                  </div>
                </div>
              )}

              {teamView === 'trustees' && (
                <div className="bg-white p-8 rounded-xl shadow-xl mb-6">
                  <div className="grid md:grid-cols-3 gap-8">
                    <div className="text-center bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300">
                      <img src="/images/Nixon_Odari.jpeg" alt="Nixon Odari" className="w-32 h-32 rounded-full object-cover border-4 border-black shadow-lg mx-auto mb-4" />
                      <h4 className="text-lg font-bold text-black mb-1">Nixon Odari</h4>
                      <p className="text-gray-700 font-semibold text-sm mb-3">Board Trustee</p>
                      <div className="w-8 h-0.5 bg-black mx-auto mb-3"></div>
                      <p className="text-gray-500 text-sm">Bio coming soon</p>
                    </div>
                    <div className="text-center bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300">
                      <img src="/images/Jared_Osoro.jpeg" alt="Jared Osoro" className="w-32 h-32 rounded-full object-cover border-4 border-black shadow-lg mx-auto mb-4" />
                      <h4 className="text-lg font-bold text-black mb-1">Jared Osoro</h4>
                      <p className="text-gray-700 font-semibold text-sm mb-3">Board Trustee</p>
                      <div className="w-8 h-0.5 bg-black mx-auto mb-3"></div>
                      <p className="text-gray-500 text-sm">Bio coming soon</p>
                    </div>
                    <div className="text-center bg-gray-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300">
                      <img src="/images/Harris_Kukundakwe.jpeg" alt="Harris Kukundakwe" className="w-32 h-32 rounded-full object-cover border-4 border-black shadow-lg mx-auto mb-4" />
                      <h4 className="text-lg font-bold text-black mb-1">Harris Kukundakwe</h4>
                      <p className="text-gray-700 font-semibold text-sm mb-3">Board Trustee</p>
                      <div className="w-8 h-0.5 bg-black mx-auto mb-3"></div>
                      <p className="text-gray-500 text-sm">Bio coming soon</p>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Join CTA */}
            <div className="bg-gradient-to-r from-red-600 to-black text-white py-16">
              <div className="max-w-4xl mx-auto px-4 text-center">
                <h3 className="text-3xl font-bold mb-4">Want to Join Our Team?</h3>
                <p className="text-xl mb-8 text-gray-200">Passionate about youth empowerment and global affairs?</p>
                <button onClick={() => setCurrentPage('join')} className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition shadow-xl">Apply Now</button>
              </div>
            </div>

          </div>
        )}
        {currentPage === 'newsletter' && (
          <div className="max-w-4xl mx-auto px-4 py-16">
            <h2 className="text-4xl font-bold text-black mb-12 text-center">Newsletter</h2>
            
            <div className="bg-white p-8 rounded-lg shadow-xl mb-12">
              <h3 className="text-2xl font-bold text-black mb-6">Subscribe with Preferences</h3>
              <form onSubmit={handleNewsletterPreferencesSubmit}>
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
                  <input
                    type="email"
                    value={newsletterPreferences.email}
                    onChange={(e) => setNewsletterPreferences({...newsletterPreferences, email: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Frequency</label>
                  <select
                    value={newsletterPreferences.frequency}
                    onChange={(e) => setNewsletterPreferences({...newsletterPreferences, frequency: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-3">Topics of Interest</label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {['Foreign Policy', 'Climate Change', 'Peace & Security', 'Good Governance', 'Youth Leadership', 'Economic Development'].map((topic) => (
                      <label key={topic} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newsletterPreferences.topics.includes(topic)}
                          onChange={() => toggleNewsletterTopic(topic)}
                          className="w-5 h-5 text-red-600"
                        />
                        <span className="text-gray-700">{topic}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={newsletterStatus === 'submitting'}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition disabled:bg-gray-400"
                >
                  {newsletterStatus === 'submitting' ? 'Subscribing...' : 'Subscribe Now'}
                </button>

                {newsletterStatus === 'success' && (
                  <div className="mt-4 bg-green-100 text-green-800 p-3 rounded-lg font-bold">
                    Successfully subscribed with your preferences!
                  </div>
                )}
                {newsletterStatus === 'error' && (
                  <div className="mt-4 bg-red-100 text-red-800 p-3 rounded-lg font-bold">
                    ÃƒÂ¢Ã‚ÂÃ…â€™ Subscription failed. Please try again.
                  </div>
                )}
              </form>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-black mb-6">Newsletter Archives</h3>
              <div className="space-y-4">
                {newsletterArchives.map((archive) => (
                  <div key={archive.id} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-xl font-bold text-black">{archive.title}</h4>
                      <button className="text-red-600 hover:text-red-800">
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-gray-600 mb-2">{archive.excerpt}</p>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {archive.date}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PDF-like Article Viewer */}
        {selectedArticle && (
          <div className="fixed inset-0 bg-gray-900 z-50 overflow-y-auto">
            <div className="min-h-screen bg-white">
              {/* Close button */}
              <div className="sticky top-0 bg-white border-b border-gray-200 shadow-sm z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                  <button 
                    onClick={() => closeArticle()}
                    className="flex items-center gap-2 text-gray-600 hover:text-red-600 font-semibold transition"
                  >
                    <X className="w-5 h-5" />
                    Close
                  </button>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{selectedArticle.readTime}</span>
                  </div>
                </div>
              </div>

              {/* PDF-like Document */}
              <div className="max-w-4xl mx-auto px-8 py-12 bg-white shadow-2xl my-8">
                {/* Header */}
                <div className="border-b-4 border-red-600 pb-8 mb-8">
                  <div className="mb-4">
                    <span className="bg-red-600 text-white px-4 py-2 rounded text-sm font-semibold uppercase tracking-wide">
                      {selectedArticle.category}
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-black leading-tight mb-6">
                    {selectedArticle.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-6 text-gray-600">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Author</p>
                        <p className="font-semibold text-black">{selectedArticle.author}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Published</p>
                        <p className="font-semibold text-black">{selectedArticle.date}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Featured Image */}
                {selectedArticle.image && (
                  <div className="mb-10">
                    <img 
                      src={selectedArticle.image} 
                      alt={selectedArticle.title}
                      className="w-full h-96 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                )}

                {/* Abstract/Excerpt */}
                <div className="bg-gray-50 border-l-4 border-red-600 p-6 mb-10">
                  <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Abstract</h2>
                  <p className="text-gray-700 leading-relaxed">{selectedArticle.excerpt}</p>
                </div>

                {/* Main Content */}
                <div className="prose prose-lg max-w-none">
                  <div className="text-gray-800 leading-relaxed text-justify space-y-6">
                    {selectedArticle.fullContent.split('\n\n').map((paragraph, idx) => (
                      paragraph.trim().startsWith('## ') ? (
                        <h3 key={idx} className="text-2xl font-bold text-black mt-8 mb-2">
                          {paragraph.trim().replace('## ', '')}
                        </h3>
                      ) : (
                        <p key={idx} className="text-lg leading-relaxed">
                          {paragraph}
                        </p>
                      )
                    ))}
                  </div>
                </div>

                {/* PDF Download Button */}
                {selectedArticle.pdfUrl && (
                  <div className="mt-8">
                    <a 
                      href={selectedArticle.pdfUrl} 
                      download 
                      className="inline-flex items-center bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700 transition"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Full Study (PDF)
                    </a>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-16 pt-8 border-t-2 border-gray-200">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-semibold text-black mb-2">About the Author</h3>
                     <p className="text-gray-600">{selectedArticle.authorBio || selectedArticle.author}</p>
                  </div>
                </div>

                {/* Close button at bottom */}
                <div className="mt-12 text-center">
                  <button 
                    onClick={() => closeArticle()}
                    className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition font-semibold shadow-lg"
                  >
                    Back to Insights
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedActivity && (
          <div className="fixed inset-0 bg-gray-900 z-50 overflow-y-auto">
            <div className="min-h-screen bg-white">
              <div className="sticky top-0 bg-white border-b border-gray-200 shadow-sm z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                  <button
                    onClick={() => setSelectedActivity(null)}
                    className="flex items-center gap-2 text-gray-600 hover:text-red-600 font-semibold transition"
                  >
                    <X className="w-5 h-5" />
                    Close
                  </button>
                  <span className="text-sm text-gray-500">{selectedActivity.date}</span>
                </div>
              </div>

              <div className="max-w-4xl mx-auto px-8 py-12 bg-white shadow-2xl my-8">
                <img src={selectedActivity.image} alt={selectedActivity.title} className="w-full h-80 object-cover rounded-lg mb-8" />
                <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">{selectedActivity.category}</span>
                <h1 className="text-3xl font-bold text-black mt-4 mb-6">{selectedActivity.title}</h1>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">{selectedActivity.description}</p>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Globe className="w-5 h-5 text-red-600" />
                    <span className="font-semibold">Location:</span> {selectedActivity.location}
                  </div>
                  {selectedActivity.attendees && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-5 h-5 text-red-600" />
                      <span className="font-semibold">Participants:</span> {selectedActivity.attendees}
                    </div>
                  )}
                </div>

                {selectedActivity.outcomes && (
                  <div className="bg-gray-50 rounded-lg p-6 mb-8">
                    <h3 className="font-semibold text-black mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Key Outcomes
                    </h3>
                    <ul className="space-y-2">
                      {selectedActivity.outcomes.map((outcome, idx) => (
                        <li key={idx} className="text-gray-700 flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-12 text-center">
                  <button
                    onClick={() => setSelectedActivity(null)}
                    className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition font-semibold shadow-lg"
                  >
                    Back to Insights
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {currentPage === 'programs' && (
          <div className="max-w-7xl mx-auto px-4 py-16">
            <h2 className="text-4xl font-bold text-black mb-12 text-center">Our Programs</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { title: "Safe Space Program", desc: "Virtual dialogue platform for inclusive engagement on governance issues", icon: Mail },
                { title: "Roundtable Discussions", desc: "Solution-oriented dialogue with policymakers and experts", icon: Users }


              ].map((program, idx) => (
                <div key={idx} className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition">
                  <program.icon className="w-16 h-16 text-red-600 mb-4" />
                  <h3 className="text-2xl font-bold text-black mb-3">{program.title}</h3>
                  <p className="text-gray-600 text-lg">{program.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {currentPage === 'programs' && (
          <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
              <div className="max-w-6xl mx-auto px-4">
                <h1 className="text-5xl font-bold mb-6">Our Programs</h1>
                <p className="text-xl max-w-3xl">Empowering African youth through research, policy analysis, leadership development, and international engagement</p>
              </div>
            </div>

            {/* Programs Grid */}
            <div className="max-w-6xl mx-auto px-4 py-16">
              
              {/* Research Fellowship */}
              {/* Safe Space Program */}
              <div className="bg-white rounded-lg shadow-xl p-8 mb-8 hover:shadow-2xl transition">
                <div className="flex items-start gap-6">
                  <Mail className="w-16 h-16 text-red-600 flex-shrink-0" />
                  <div className="flex-grow">
                    <h2 className="text-3xl font-bold text-black mb-4">Safe Space Program</h2>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h3 className="font-semibold text-lg text-red-600 mb-2">Overview</h3>
                        <p className="text-gray-700">
                          Safe Space is a virtual, dialogue-driven initiative by Youth in Governance and Global Affairs (YIGA) that provides young people with an inclusive and respectful platform to engage on key governance issues at both domestic and international levels. Designed primarily as a safe and moderated environment for open conversation, the program enables participants to critically engage on questions of leadership, public policy, accountability, democratic participation, peace, and global affairs.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-red-600 mb-2">What You'll Gain</h3>
                        <ul className="text-gray-700 space-y-2">
                          <li>&mdash; Safe and moderated environment for open dialogue</li>
                          <li>&mdash; Critical engagement on leadership and public policy</li>
                          <li>&mdash; Platform to discuss accountability and democracy</li>
                          <li>&mdash; Understanding of peace and global affairs</li>
                          <li>&mdash; Respectful peer-to-peer learning experience</li>
                          <li>&mdash; Virtual accessibility from anywhere</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded">
                      <div>
                        <p className="font-semibold text-sm text-gray-600">Format</p>
                        <p className="text-black">Virtual discussions</p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-600">Eligibility</p>
                        <p className="text-black">All young people interested in governance</p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-600">Frequency</p>
                        <p className="text-black">Monthly sessions</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Roundtable Discussions */}
              <div className="bg-white rounded-lg shadow-xl p-8 mb-8 hover:shadow-2xl transition">
                <div className="flex items-start gap-6">
                  <Users className="w-16 h-16 text-red-600 flex-shrink-0" />
                  <div className="flex-grow">
                    <h2 className="text-3xl font-bold text-black mb-4">Roundtable Discussions</h2>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h3 className="font-semibold text-lg text-red-600 mb-2">Overview</h3>
                        <p className="text-gray-700">
                          Roundtable Discussions are a core convening platform under Youth in Governance and Global Affairs (YIGA), bringing together youth leaders, scholars, practitioners, policymakers, and civil society actors for focused, solution-oriented dialogue. These discussions are designed to be interactive and participatory, creating space for in-depth engagement, exchange of perspectives, and collaborative problem-solving. YIGA's roundtables are anchored in the organization's key focus areas, including foreign policy, governance, peace and security, climate change, culture, and sustainable development.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-red-600 mb-2">What You'll Gain</h3>
                        <ul className="text-gray-700 space-y-2">
                          <li>&mdash; Direct engagement with policymakers and experts</li>
                          <li>&mdash; Solution-oriented collaborative problem-solving</li>
                          <li>&mdash; Contribute to policy briefs and advocacy initiatives</li>
                          <li>&mdash; Participate in knowledge production and research</li>
                          <li>&mdash; Network with scholars and practitioners</li>
                          <li>&mdash; Influence practical ideas with policy-relevant impact</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded">
                      <div>
                        <p className="font-semibold text-sm text-gray-600">Format</p>
                        <p className="text-black">Interactive roundtables</p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-600">Eligibility</p>
                        <p className="text-black">Youth leaders, scholars, practitioners</p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-600">Outcome</p>
                        <p className="text-black">Policy briefs & research outputs</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Youth Leadership */}



















































































              {/* Call to Action */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg p-12 text-center mt-12">
                <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">Join thousands of young Africans making an impact through YIGA programs</p>
                <button 
                  onClick={() => setCurrentPage('join')}
                  className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition inline-flex items-center gap-2"
                >
                  Apply Now <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}


        {currentPage === 'join' && (
          <div className="max-w-3xl mx-auto px-4 py-16">
            <h2 className="text-4xl font-bold text-black mb-8 text-center">Join YIGA</h2>
            <div className="bg-white p-8 rounded-lg shadow-xl">
              {submitStatus === 'success' && (
                <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                  Application submitted successfully!
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  ÃƒÂ¢Ã‚ÂÃ…â€™ Submission failed. Please try again.
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Full Name</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Motivation</label>
                  <textarea
                    value={formData.motivation}
                    onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                    rows="5"
                    required
                  ></textarea>
                </div>






















                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition disabled:bg-gray-400"
                >
                  {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Past Activities Page */}
        {currentPage === 'activities' && (
          <div className="min-h-screen bg-gray-50">
            <div className="bg-gradient-to-r from-red-600 to-black text-white py-20">
              <div className="max-w-6xl mx-auto px-4">
                <h1 className="text-5xl font-bold mb-6">Past Activities</h1>
                <p className="text-xl max-w-3xl">Highlights from our events, workshops, conferences, and initiatives driving youth engagement in governance and international affairs</p>
              </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-16">
              <div className="grid gap-8">
                {pastActivities.map((activity) => (
                  <div key={activity.id} className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition">
                    <div className="grid md:grid-cols-3 gap-6">
                      <img src={activity.image} alt={activity.title} className="w-full h-64 md:h-full object-cover" />
                      <div className="md:col-span-2 p-6">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                            {activity.category}
                          </span>
                          <span className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {activity.date}
                          </span>
                        </div>
                        <h2 className="text-3xl font-bold text-black mb-3">{activity.title}</h2>
                        <p className="text-gray-700 mb-4">{activity.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Globe className="w-5 h-5 text-red-600" />
                            <span className="font-semibold">Location:</span> {activity.location}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Users className="w-5 h-5 text-red-600" />
                            <span className="font-semibold">Participants:</span> {activity.attendees}
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="font-semibold text-black mb-2 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            Key Outcomes
                          </h3>
                          <ul className="space-y-2">
                            {activity.outcomes.map((outcome, idx) => (
                              <li key={idx} className="text-gray-700 flex items-start gap-2">
                                <ArrowRight className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                                {outcome}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Articles Page */}
        {currentPage === 'research' && (
          <div className="min-h-screen bg-gray-50">
            <div className="bg-gradient-to-r from-red-600 to-black text-white py-20">
              <div className="max-w-6xl mx-auto px-4">
                <h1 className="text-5xl font-bold mb-6">Research Publications</h1>
                <p className="text-xl max-w-3xl">Research publications, policy briefs, and analytical pieces from our team of experts and fellows</p>
              </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-16">

              {/* Article Type Selector */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                <button
                  onClick={() => setArticleView('research')}
                  className={`text-left p-6 rounded-lg shadow-lg transition border-2 ${articleView === 'research' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-black border-transparent hover:shadow-2xl'}`}
                >
                  <h2 className="text-xl font-bold mb-1">Research Articles</h2>
                  <p className={`text-sm ${articleView === 'research' ? 'text-red-100' : 'text-gray-500'}`}>Analytical pieces from our team of experts and fellows</p>
                </button>
                <button
                  onClick={() => setArticleView('briefs')}
                  className={`text-left p-6 rounded-lg shadow-lg transition border-2 ${articleView === 'briefs' ? 'bg-black text-white border-black' : 'bg-white text-black border-transparent hover:shadow-2xl'}`}
                >
                  <h2 className="text-xl font-bold mb-1">Policy Briefs</h2>
                  <p className={`text-sm ${articleView === 'briefs' ? 'text-gray-200' : 'text-gray-500'}`}>Evidence-based recommendations from research and roundtables</p>
                </button>
                <button
                  onClick={() => setArticleView('studies')}
                  className={`text-left p-6 rounded-lg shadow-lg transition border-2 ${articleView === 'studies' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-black border-transparent hover:shadow-2xl'}`}
                >
                  <h2 className="text-xl font-bold mb-1">Research Study Reports</h2>
                  <p className={`text-sm ${articleView === 'studies' ? 'text-red-100' : 'text-gray-500'}`}>In-depth studies and full research findings</p>
                </button>
                <button
                  onClick={() => setArticleView('activities')}
                  className={`text-left p-6 rounded-lg shadow-lg transition border-2 ${articleView === 'activities' ? 'bg-black text-white border-black' : 'bg-white text-black border-transparent hover:shadow-2xl'}`}
                >
                  <h2 className="text-xl font-bold mb-1">Activity Reports</h2>
                  <p className={`text-sm ${articleView === 'activities' ? 'text-gray-200' : 'text-gray-500'}`}>Highlights from our events, workshops and initiatives</p>
                </button>
              </div>

              {articleView === 'research' && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles && articles.filter(a => a.section === 'article').map((article) => (
                    <div key={article.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition">
                      <img src={article.image} alt={article.title} className="w-full h-48 object-cover" />
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">{article.category}</span>
                          <span className="text-sm text-gray-500">{article.readTime}</span>
                        </div>
                        <h3 className="text-xl font-bold text-black mb-3 line-clamp-2">{article.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Users className="w-4 h-4" />
                          <span>{article.author}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{article.date}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {article.tags.map((tag, idx) => (
                            <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">{tag}</span>
                          ))}
                        </div>
                        <button onClick={() => openArticle(article)} className="text-red-600 font-semibold hover:text-red-700 flex items-center gap-2">
                          Read Article <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {articleView === 'briefs' && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles && articles.filter(a => a.section === 'brief').map((article) => (
                    <div key={article.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition">
                      <img src={article.image} alt={article.title} className="w-full h-48 object-cover" />
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="bg-black text-white px-3 py-1 rounded-full text-xs font-semibold">{article.category}</span>
                          <span className="text-sm text-gray-500">{article.readTime}</span>
                        </div>
                        <h3 className="text-xl font-bold text-black mb-3 line-clamp-2">{article.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{article.date}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {article.tags.map((tag, idx) => (
                            <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">{tag}</span>
                          ))}
                        </div>
                        <div className="flex gap-3">
                          <button onClick={() => openArticle(article)} className="text-red-600 font-semibold hover:text-red-700 flex items-center gap-2">
                            Read Brief <ArrowRight className="w-4 h-4" />
                          </button>
                          {article.pdfUrl && (
                            <a href={article.pdfUrl} download className="text-black font-semibold hover:text-gray-700 flex items-center gap-2">
                              <Download className="w-4 h-4" /> PDF
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {articleView === 'studies' && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles && articles.filter(a => a.section === 'study').map((article) => (
                    <div key={article.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition">
                      <img src={article.image} alt={article.title} className="w-full h-48 object-cover" />
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">{article.category}</span>
                          <span className="text-sm text-gray-500">{article.readTime}</span>
                        </div>
                        <h3 className="text-xl font-bold text-black mb-3 line-clamp-2">{article.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Users className="w-4 h-4" />
                          <span>{article.author}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{article.date}</span>
                        </div>
                        <div className="flex gap-3">
                          <button onClick={() => openArticle(article)} className="text-red-600 font-semibold hover:text-red-700 flex items-center gap-2">
                            Read Report <ArrowRight className="w-4 h-4" />
                          </button>
                          {article.pdfUrl && (
                            <a href={article.pdfUrl} download className="text-black font-semibold hover:text-gray-700 flex items-center gap-2">
                              <Download className="w-4 h-4" /> PDF
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {articleView === 'activities' && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {pastActivities.map((activity) => (
                    <div key={activity.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition">
                      <img src={activity.image} alt={activity.title} className="w-full h-48 object-cover" />
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">{activity.category}</span>
                          <span className="text-sm text-gray-500">{activity.date}</span>
                        </div>
                        <h3 className="text-xl font-bold text-black mb-3 line-clamp-2">{activity.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{activity.description}</p>
                        <button onClick={() => setSelectedActivity(activity)} className="text-red-600 font-semibold hover:text-red-700 flex items-center gap-2">
                          Read More <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Student Partners Page */}
        {currentPage === 'partners' && (
          <div className="min-h-screen bg-gray-50">
            <div className="bg-gradient-to-r from-red-600 to-black text-white py-20">
              <div className="max-w-6xl mx-auto px-4">
                <h1 className="text-5xl font-bold mb-6">Student Partners</h1>
                <p className="text-xl">A national platform dedicated to uniting and empowering young international relations students and professionals through our student partner organizations</p>
              </div>
            </div>
            <div className="max-w-6xl mx-auto px-4 py-16">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-8 rounded-lg shadow-lg text-center hover:shadow-xl transition">
                  <h3 className="text-2xl font-bold mb-4 text-black">International Relations Students Association of Kenya (IRSAK)</h3>
                  <p className="text-gray-600">The International Relations Students Association of Kenya (IRSAK) is a national platform dedicated to uniting and empowering young international relations students and professionals. It serves as a hub for intellectual discourse, mentorship, research, and professional development, bridging the gap between academic knowledge and real-world application.</p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-lg text-center hover:shadow-xl transition">
                  <h3 className="text-2xl font-bold mb-4 text-black">Partner University 2</h3>
                  <p className="text-gray-600">Details coming soon</p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-lg text-center hover:shadow-xl transition">
                  <h3 className="text-2xl font-bold mb-4 text-black">Partner University 3</h3>
                  <p className="text-gray-600">Details coming soon</p>
                </div>
              </div>
            </div>
          </div>
        )}




























        {currentPage === 'admin' && (
          <div className="max-w-7xl mx-auto px-4 py-16">
            {!isLoggedIn ? (
              <div className="max-w-md mx-auto">
                <h2 className="text-3xl font-bold text-black mb-8 text-center">Admin Login</h2>
                <form onSubmit={handleLogin} className="bg-white p-8 rounded-lg shadow-xl">
                  {loginError && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      {loginError}
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">Username</label>
                    <input
                      type="text"
                      value={loginData.username}
                      onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                      required
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-700 font-semibold mb-2">Password</label>
                    <input
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition disabled:bg-gray-400"
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </button>
                </form>
              </div>
            ) : (
              <div>
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-black mb-2">Admin Dashboard</h2>
                  <p className="text-gray-600">Welcome, {username} ({userRole})</p>
                </div>


                {/* Stats Dashboard */}
                <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-red-600 text-center">
                    <div className="text-3xl font-bold text-red-600">{applications.length}</div>
                    <div className="text-gray-600 text-sm mt-1">Total Applications</div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-yellow-500 text-center">
                    <div className="text-3xl font-bold text-yellow-500">{applications.filter(a => a.status === 'pending').length}</div>
                    <div className="text-gray-600 text-sm mt-1">Pending</div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-green-500 text-center">
                    <div className="text-3xl font-bold text-green-500">{applications.filter(a => a.status === 'approved').length}</div>
                    <div className="text-gray-600 text-sm mt-1">Approved</div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-black text-center">
                    <div className="text-3xl font-bold text-black">{applications.filter(a => a.status === 'rejected').length}</div>
                    <div className="text-gray-600 text-sm mt-1">Rejected</div>
                  </div>
                </div>

                {/* Weekly Stats */}
                <div className="mb-8 bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-black mb-4">?? Weekly Report Snapshot</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-gray-500 text-sm">New Applications This Week</p>
                      <p className="text-2xl font-bold text-red-600">{applications.filter(a => { const d = new Date(a.created_at); const now = new Date(); const weekAgo = new Date(now - 7*24*60*60*1000); return d > weekAgo; }).length}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">New Applications This Month</p>
                      <p className="text-2xl font-bold text-red-600">{applications.filter(a => { const d = new Date(a.created_at); const now = new Date(); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }).length}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Report Generated</p>
                      <p className="text-lg font-bold text-black">{new Date().toLocaleDateString('en-KE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                </div>
                {userRole === 'superadmin' && (
                  <div className="mb-12 bg-white p-8 rounded-lg shadow-xl">
                    <h3 className="text-2xl font-bold text-black mb-6">Create New Admin</h3>
                    {adminError && (
                      <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {adminError}
                      </div>
                    )}
                    {adminSuccess && (
                      <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                        {adminSuccess}
                      </div>
                    )}
                    <form onSubmit={createAdmin} className="grid md:grid-cols-4 gap-4">
                      <input
                        type="text"
                        placeholder="Username"
                        value={newAdmin.username}
                        onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                        className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                        className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                        required
                      />
                      <select
                        value={newAdmin.role}
                        onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                        className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-600"
                      >
                        <option value="admin">Admin</option>
                        <option value="superadmin">Superadmin</option>
                      </select>
                      <button
                        type="submit"
                        className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-700 transition flex items-center justify-center"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create
                      </button>
                    </form>

                    <div className="mt-8">
                      <h4 className="text-xl font-bold text-black mb-4">Manage Admins</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left">Username</th>
                              <th className="px-4 py-3 text-left">Role</th>
                              <th className="px-4 py-3 text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {admins.map((admin) => (
                              <tr key={admin.id} className="border-b">
                                <td className="px-4 py-3">{admin.username}</td>
                                <td className="px-4 py-3">
                                  <select
                                    value={admin.role}
                                    onChange={(e) => updateAdminRole(admin.id, e.target.value)}
                                    className="px-3 py-1 border rounded"
                                    disabled={admin.username === "Shawn"}
                                  >
                                    <option value="admin">Admin</option>
                                    <option value="superadmin">Superadmin</option>
                                  </select>
                                </td>
                                <td className="px-4 py-3">
                                  {admin.username !== username && (
                                    <button
                                      onClick={() => deleteAdmin(admin.id)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white p-8 rounded-lg shadow-xl">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-black">Applications</h3>
                    <button
                      onClick={fetchApplications}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition"
                    >
                      Refresh
                    </button>
                  </div>

                  {/* Search Box */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="?? Search by name, email or country..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                    {searchQuery && (
                      <p className="text-sm text-gray-500 mt-1">
                        Showing {applications.filter(app =>
                          app.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.status?.toLowerCase().includes(searchQuery.toLowerCase())
                        ).length} results for "{searchQuery}"
                      </p>
                    )}
                  </div>
                  {loading ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 mx-auto text-gray-400 animate-spin" />
                      <p className="mt-4 text-gray-600">Loading applications...</p>
                    </div>
                  ) : applications.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">No applications yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left">Name</th>
                            <th className="px-4 py-3 text-left">Email</th>
                            <th className="px-4 py-3 text-left">Country</th>
                            <th className="px-4 py-3 text-left">Program</th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {applications.filter(app => !searchQuery || app.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || app.email?.toLowerCase().includes(searchQuery.toLowerCase()) || app.country?.toLowerCase().includes(searchQuery.toLowerCase()) || app.status?.toLowerCase().includes(searchQuery.toLowerCase())).map((app) => (
                            <tr key={app.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-3">{app.full_name}</td>
                              <td className="px-4 py-3">{app.email}</td>
                              <td className="px-4 py-3">{app.country}</td>
                              <td className="px-4 py-3">{app.program}</td>
                              <td className="px-4 py-3">
                                <select
                                  value={app.status}
                                  onChange={(e) => updateApplicationStatus(app.id, e.target.value, app.email, app.full_name)}
                                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    app.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="approved">Approved</option>
                                  <option value="rejected">Rejected</option>
                                </select>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => deleteApplication(app.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                <div className="bg-white rounded-lg shadow-xl p-6 mt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-black">Newsletter Subscribers ({newsletterSubscribers.length})</h2>
                    <button
                      onClick={() => sendNewsletter()}
                      className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition"
                    >
                      Send Newsletter
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="px-4 py-3">Email</th>
                          <th className="px-4 py-3">Subscribed</th>
                          <th className="px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletterSubscribers.map((sub) => (
                          <tr key={sub.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3">{sub.email}</td>
                            <td className="px-4 py-3">{sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : ''}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => deleteNewsletterSubscriber(sub.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Social Media Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">YIGA</h3>
                <p className="text-gray-400">Youth in Governance and Global Affairs</p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">Contact Us</h3>
                <div className="text-gray-400 space-y-2">
                  <p>Email: info@yiga.africa</p>
                  <p>Phone: +254 794 538 341 / +254 740 278 055</p>
                  <p>Location: Nairobi, Kenya</p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a href="https://www.facebook.com/share/1AaGHcz5AJ/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a href="https://x.com/irsakofficial" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/irsakofficial/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a href="https://www.linkedin.com/company/international-relations-students-association-kenya/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                    <Linkedin className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2025 YIGA. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
export default App;
































